/* eslint-disable @typescript-eslint/no-explicit-any */
import { useGalleryCtx } from "@/contexts/gallery";
import { useLocaleCtx } from "@/contexts/locale";
import { getGalleryPagePerformances } from "@/services/gallery/gallery.client";
import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { filter, PerformanceSectionContext } from "../types/common";
import { useSearchParams } from "next/navigation";
import { YearQuery } from "../common/filter";

const PerformanContext = createContext<PerformanceSectionContext | undefined>(undefined);

export const usePerformance = () => {
  const context = useContext(PerformanContext);

  if (!context) {
    throw new Error("usePerformance must be used within a PerformanceProvider");
  }

  return context;
};

export const PerformanceProvider = ({ data, children }: { data: any; children: ReactNode }) => {
  const params = useSearchParams();

  const initialYear = {
    title: params.get(YearQuery.PERFORMANCE_YEAR) || data.year_filter_text.years[4],
    value: params.get(YearQuery.PERFORMANCE_YEAR) || "",
  };

  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(6);
  const [year, setYear] = useState<filter>(initialYear);
  const [type, setType] = useState<filter>({
    title: data.dropdown_text.values[2],
    value: "",
  });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [usedPerformancesCount, setUsedPerformancesCount] = useState(0);
  const [initialDataEnded, setInitialDataEnded] = useState(false);

  const { lang } = useLocaleCtx();
  const { setPerformances, performances } = useGalleryCtx();

  const appendUniquePerformances = (newData: any) => {
    setPerformances((prev: any) => {
      const existingIds = prev.map((performance: any) => performance.id);
      const filteredData = newData.filter(
        (performance: any) => !existingIds.includes(performance.id)
      );
      return [...prev, ...filteredData];
    });
  };

  const fetchMorePerformances = async () => {
    setLoadingMore(true);

    const isFiltered = year.value !== "" || type.value !== "";

    if (isFiltered) {
      // If filters are applied, fetch from the API
      const galleryData = await getGalleryPagePerformances({
        lang,
        pageNo,
        pageSize,
        year: year.value,
        type: type.value,
      });

      if (galleryData) {
        if (galleryData.data.length < 6) {
          setReachedEnd(true);
        }

        appendUniquePerformances(galleryData.data);
      }
    } else {
      // Check if there are remaining performances from the initial data
      const remainingPerformances = data.performances.slice(
        usedPerformancesCount,
        usedPerformancesCount + 6
      );

      if (remainingPerformances.length > 0) {
        setPerformances((prev: any) => [...prev, ...remainingPerformances]);
        setUsedPerformancesCount((prev) => prev + remainingPerformances.length);
      } else {
        setInitialDataEnded(true);
        setPageNo(1);
      }
    }

    setLoadingMore(false);
  };

  const fetchNewPerformances = async () => {
    setLoading(true);
    setReachedEnd(false);
    setInitialDataEnded(false);

    if (pageNo === 1 && year.value === "" && type.value === "") {
      const initialPerformances = data.performances.slice(0, 6);
      setPerformances(initialPerformances);
      setUsedPerformancesCount(initialPerformances.length);
      setLoading(false);

      return;
    }

    const galleryData = await getGalleryPagePerformances({
      lang,
      pageNo: 1,
      pageSize,
      year: year.value,
      type: type.value,
    });

    if (galleryData) {
      setPerformances([...galleryData.data]);
      setUsedPerformancesCount(0);
    }

    setLoading(false);
  };

  useEffect(() => {
    // Once initial data is exhausted, revert to fetching normally

    if (initialDataEnded) {
      (async () => {
        const galleryData = await getGalleryPagePerformances({
          lang,
          pageNo,
          pageSize,
          year: year.value,
          type: type.value,
        });

        if (galleryData) {
          if (galleryData.data.length < 6) {
            setReachedEnd(true);
          }
          appendUniquePerformances(galleryData.data);
        }
      })();

      return;
    }

    if (pageNo > 1) {
      fetchMorePerformances();
    }
  }, [pageNo]);

  useEffect(() => {
    setPageNo(1);
    fetchNewPerformances();
  }, [lang, year, type]);

  const value = {
    pageNo,
    pageSize,
    year,
    type,
    loading,
    loadingMore,
    setPageNo,
    setYear,
    setType,
    setLoading,
    setLoadingMore,
    usedPerformancesCount,
    setUsedPerformancesCount,
    performances,
    reachedEnd,
  };

  return <PerformanContext.Provider value={value}>{children}</PerformanContext.Provider>;
};
